import { AnyObject } from '@store/types';
import uuid from 'uuid';

import {
    DrugFavoriteDataParams,
    FavoriteData,
    FavoriteListQuery,
    FavoriteType,
    ReferralFavoriteDataParams,
} from 'chaika-frontend-types/frontend-cms';

import { FavoriteApi } from '@api/FavoriteApi';
import { store } from '@store';
import { serviceService } from '@services/services/serviceService';
import { userService } from '@user/services/userService';
import { organizationService } from '@organization/service/organizaion/organizationService';
import { laboratoryService } from '@lab/service/laboratoryService';
import { toArrayFromIterable } from '@utils/toArrayFromIterable';
import { assertIsDefined } from '@utils/assertIsDefined';
import { dictionaryService } from '@template/services/dictionary/dictionaryService';
import { queryClient } from '@store/queryClient';
import { referralFavoriteKeys } from '@common/ReferralMenu/queries/referralFavoriteKeys';
import { drugFavoritesKeys } from '@common/CreateDrug/queries/drugFavoritesQueries';

const FavoriteTypeDic: Partial<Record<FavoriteType, string>> = {
    [FavoriteType.Referral]: 'referralFavoriteList',
    [FavoriteType.Drug]: 'drugFavoriteList',
    [FavoriteType.Recommendation]: 'recommendationFavoriteList',
    [FavoriteType.EntryType]: 'entryTypeFavoriteList',
    [FavoriteType.Office]: 'officeFavoriteList',
};

interface RemoveDataItemParams {
    removeByFieldName: string;
    removeByFieldValue: string;
}

class FavoriteService {
    public async load({ types, ...params }: FavoriteListQuery): Promise<void> {
        const { id, favorites } = store.authUser;
        const notLoadedTypes = types.filter(type => !favorites.loadedTypes.has(type));
        if (notLoadedTypes.length > 0) {
            const favoriteList = await FavoriteApi.getList(id, { types: notLoadedTypes, ...params });
            await this.loadRelations(favoriteList);

            notLoadedTypes.forEach(type => {
                (favorites as AnyObject)[getFavoriteListName(type)].set(
                    favoriteList.filter(item => item.type === type),
                );
            });
        }
    }

    public async loadFavorite(favoriteId: string): Promise<void> {
        const { id, favorites } = store.authUser;
        const favorite = await FavoriteApi.get(id, favoriteId);
        const { type } = favorite;

        if (!(favorites as AnyObject)[getFavoriteListName(type)].has(favoriteId)) {
            (favorites as AnyObject)[getFavoriteListName(type)].add([favorite]);
        }
    }

    public async createFavorite(favorite: Omit<FavoriteData, 'id' | 'userId'>): Promise<void> {
        await this.create({
            id: uuid(),
            userId: store.authUser.id,
            ...favorite,
        });
    }

    public async create(favorite: Omit<FavoriteData, 'order'>): Promise<void> {
        const { id, favorites } = store.authUser;
        const { type } = favorite;

        let serviceIds: string[] = [];
        if (type === FavoriteType.Referral) {
            serviceIds = Array.isArray(favorite.data)
                ? favorite.data.map(({ serviceId }: any) => serviceId)
                : [(favorite.data as ReferralFavoriteDataParams).serviceId];
        }

        await Promise.all([
            FavoriteApi.create(id, { favorite }),
            serviceIds.length > 0 && serviceService.loadByIds(serviceIds),
        ]);
        (favorites as AnyObject)[getFavoriteListName(type)].add([{ order: 0, ...favorite }]);

        if (type === FavoriteType.Drug) {
            this.reloadDrugFavorites();
        }

        if (type === FavoriteType.Referral) {
            this.reloadReferralFavorites();
        }
    }

    public async update(favoriteId: string, params: Partial<FavoriteData>, type: FavoriteType): Promise<void> {
        const { id, favorites } = store.authUser;

        await FavoriteApi.update(id, favoriteId, { favorite: params });
        (favorites as AnyObject)[getFavoriteListName(type)].update(favoriteId, params);

        if (type === FavoriteType.Drug) {
            this.reloadDrugFavorites();
        }

        if (type === FavoriteType.Referral) {
            this.reloadReferralFavorites();
        }
    }

    public async remove(favoriteId: string, type: FavoriteType): Promise<void> {
        const { id, favorites } = store.authUser;

        await FavoriteApi.remove(id, favoriteId);
        (favorites as AnyObject)[getFavoriteListName(type)].remove(favoriteId);

        if (type === FavoriteType.Referral) {
            this.reloadReferralFavorites();
        }

        if (type === FavoriteType.Drug) {
            this.reloadDrugFavorites();
        }
    }

    public async removeDataItem(
        favoriteId: string,
        { removeByFieldName, removeByFieldValue }: RemoveDataItemParams,
        type: FavoriteType,
    ): Promise<void> {
        const { id: userId, favorites } = store.authUser;

        const favorite = (favorites as AnyObject)[getFavoriteListName(type)].get(favoriteId);

        const data = (favorite.data as any[]).filter(params => params[removeByFieldName] !== removeByFieldValue);

        await FavoriteApi.update(userId, favoriteId, { favorite: { data } });
        (favorites as AnyObject)[getFavoriteListName(type)].update(favoriteId, { data });

        if (type === FavoriteType.Drug) {
            this.reloadDrugFavorites();
        }

        if (type === FavoriteType.Referral) {
            this.reloadReferralFavorites();
        }
    }

    public async addReferralDataItem(favoriteId: string, referral: ReferralFavoriteDataParams): Promise<void> {
        const {
            id: userId,
            favorites: { referralFavoriteList },
        } = store.authUser;
        const favorite = referralFavoriteList.getExisting(favoriteId);
        const data = [...(favorite.data as ReferralFavoriteDataParams[]), referral];

        await Promise.all([
            FavoriteApi.update(userId, favoriteId, { favorite: { data } }),
            serviceService.loadByIds([referral.serviceId]),
        ]);

        referralFavoriteList.update(favoriteId, { data });

        this.reloadReferralFavorites();
    }

    public async updateReferralDataItem(
        favoriteId: string,
        serviceId: string,
        changes: Partial<ReferralFavoriteDataParams>,
    ): Promise<void> {
        const {
            id: userId,
            favorites: { referralFavoriteList },
        } = store.authUser;
        const favorite = referralFavoriteList.getExisting(favoriteId);
        const data = Array.isArray(favorite.data)
            ? (favorite.data as ReferralFavoriteDataParams[]).map(item =>
                  item.serviceId === serviceId ? { ...item, ...changes } : item,
              )
            : { ...(favorite.data as ReferralFavoriteDataParams), ...changes };

        await FavoriteApi.update(userId, favoriteId, { favorite: { data } });

        referralFavoriteList.update(favoriteId, { data });
        this.reloadReferralFavorites();
    }

    public async addDrugDataItem(favoriteId: string, drug: DrugFavoriteDataParams): Promise<void> {
        const {
            id: userId,
            favorites: { drugFavoriteList },
        } = store.authUser;

        const favorite = drugFavoriteList.getExisting(favoriteId);
        const data = [...(favorite.data as DrugFavoriteDataParams[]), drug];

        await Promise.all([
            FavoriteApi.update(userId, favoriteId, { favorite: { data } }),
            dictionaryService.loadDictionaryItemsByIds([drug.activeSubstance, ...(drug.tradeNameIds || [])]),
        ]);

        drugFavoriteList.update(favoriteId, { data });
        this.reloadDrugFavorites();
    }

    public async updateDrugDataItem(favoriteId: string, drug: DrugFavoriteDataParams): Promise<void> {
        const {
            id: userId,
            favorites: { drugFavoriteList },
        } = store.authUser;
        const { id: drugId } = drug;
        const favorite = drugFavoriteList.getExisting(favoriteId);

        const data = (favorite.data as DrugFavoriteDataParams[]).map(item =>
            item.id === drugId ? { ...item, ...drug } : { ...item },
        );

        await Promise.all([
            FavoriteApi.update(userId, favoriteId, { favorite: { data } }),
            dictionaryService.loadDictionaryItemsByIds([drug.activeSubstance, ...(drug.tradeNameIds || [])]),
        ]);

        drugFavoriteList.update(favoriteId, { data });
        this.reloadDrugFavorites();
    }

    public async loadRelations(favorites: FavoriteData[]): Promise<void> {
        const serviceIds = new Set<string>();
        const dictionaryItemIds = new Set<string>();
        const userIds = new Set<string | undefined>();
        favorites.forEach(favorite => {
            if (favorite.type === FavoriteType.Referral) {
                const { data } = favorite as FavoriteData<ReferralFavoriteDataParams>;
                if (Array.isArray(data)) {
                    data.forEach(params => {
                        const { serviceId, userId, authorId } = params;
                        serviceIds.add(serviceId);
                        userIds.add(userId);
                        userIds.add(authorId);
                    });
                } else {
                    const { serviceId, userId, authorId } = data;
                    serviceIds.add(serviceId);
                    userIds.add(userId);
                    userIds.add(authorId);
                }
            }
            if (favorite.type === FavoriteType.Drug) {
                const { data } = favorite as FavoriteData<DrugFavoriteDataParams>;
                if (Array.isArray(data)) {
                    data.forEach(params => {
                        const { activeSubstance, tradeNameIds = [] } = params;
                        dictionaryItemIds.add(activeSubstance);
                        tradeNameIds.forEach((id: string) => {
                            dictionaryItemIds.add(id);
                        });
                    });
                } else {
                    const {
                        data: { activeSubstance, tradeNameIds = [] },
                    } = favorite as FavoriteData<DrugFavoriteDataParams>;
                    dictionaryItemIds.add(activeSubstance);
                    tradeNameIds.forEach(id => {
                        dictionaryItemIds.add(id);
                    });
                }
            }
        });

        userIds.delete(undefined);
        await Promise.all([
            serviceService.loadByIds(toArrayFromIterable<string>(serviceIds)),
            userService.loadIfNotExist(toArrayFromIterable<string>(userIds as Set<string>)),
            dictionaryItemIds.size > 0 &&
                dictionaryService.loadDictionaryItemsByIds(toArrayFromIterable(dictionaryItemIds)),
            organizationService.loadAll(),
            laboratoryService.load(),
        ]);
    }

    public async putEntryTypeFavorite(entryTypeId: string): Promise<void> {
        const { authUser } = store;
        const favorite = authUser.favorites.entryTypeFavoriteList.getMostRecentEntryTypeRecord();

        if (favorite) {
            await this.update(
                favorite.id,
                {
                    data: {
                        entryTypeId,
                    },
                },
                FavoriteType.EntryType,
            );
        } else {
            const newFavorite = {
                id: uuid(),
                type: FavoriteType.EntryType,
                name: FavoriteType.EntryType,
                userId: authUser.id,
                data: {
                    entryTypeId,
                },
            };
            await this.create(newFavorite);
        }
    }

    public async updateOrder(groups: FavoriteData[]) {
        const favoriteOrders = groups.map(it => ({
            id: it.id,
            order: it.order ?? 0,
        }));

        await FavoriteApi.updateOrder(favoriteOrders);

        groups.forEach(group => {
            (store.authUser.favorites as AnyObject)[getFavoriteListName(groups[0].type)].update(group.id, {
                order: group.order,
            });
        });

        if (groups[0].type === FavoriteType.Drug) {
            this.reloadDrugFavorites();
        }

        if (groups[0]?.type === FavoriteType.Referral) {
            this.reloadReferralFavorites();
        }
    }

    private reloadReferralFavorites(): void {
        queryClient.invalidateQueries({ queryKey: referralFavoriteKeys.all });
    }

    private reloadDrugFavorites(): void {
        queryClient.invalidateQueries({ queryKey: drugFavoritesKeys.all });
    }
}

export const favoriteService = new FavoriteService();

function getFavoriteListName(type: FavoriteType): string {
    const listName = FavoriteTypeDic[type];
    assertIsDefined(listName, `Favorites of type ${type} are kept in the auth user favorite lists`);
    return listName;
}
